import React from 'react';
import { classes } from '../utils';
import Input from '../input';
import styles from '../../billing-address.module.scss';
import { IFormInitialState } from '../../errorLogic';
import { IPrevRefValues } from '../on-blur-handler';
import { customerInfoConstants } from '../../constants';
import InputDropdown from '../dropdown';
import { AddressInput } from './address-input';
import MaskedInputField from '../masked-input';
import { IStateProvinces } from '~/interfaces/states-provinces';

import { CheckoutBillingAddress } from '~/gql/__generated__/types';
export interface IAddressForm {
  formState: IFormInitialState;
  changeHandler: (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => void;
  onBlurHandler: (
    event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
    prevRefValues: React.RefObject<IPrevRefValues>,
    formState: IFormInitialState,
  ) => void;
  handleFocus: () => void;
  isZipcodeUpdating: boolean | undefined;
  addressFocus: React.RefObject<boolean>;
  stateProvinces: IStateProvinces[] | undefined;
  zipCode: string;
  prevRefValues: React.RefObject<IPrevRefValues>;
  updateFormState: (name: string, value: string, errorMsg?: string) => void;
  billingAddressConfigs: CheckoutBillingAddress;
}

const AddressForm: React.FC<IAddressForm> = ({
  formState,
  changeHandler,
  onBlurHandler,
  handleFocus,
  isZipcodeUpdating,
  addressFocus,
  stateProvinces,
  zipCode,
  prevRefValues,
  updateFormState,
  billingAddressConfigs,
}) => {
  const {
    firstName,
    lastName,
    emailAddress,
    phoneNumber,
    address,
    apartment,
    zipcode,
    city,
    state,
  } = customerInfoConstants.inputs;

  const addressMaxLength =
    billingAddressConfigs?.maxAddressCharacterLength ||
    address.maxCharLength ||
    55;
  const apartmentMaxLength =
    billingAddressConfigs?.maxAPTCharacterLength ||
    apartment.maxCharLength ||
    55;
  const cityMaxLength =
    billingAddressConfigs?.maxCityCharacterLength || city.maxCharLength || 55;

  const onBlurCalls = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    onBlurHandler(e, prevRefValues, formState);
    changeHandler(e);
  };
  const onBlurOnly = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    onBlurHandler(e, prevRefValues, formState);
  };
  return (
    <form className="form-with-two-columns">
      <Input
        type={firstName.type}
        id={firstName.id}
        name={firstName.name}
        label={firstName.label}
        value={formState.firstName.value}
        onChange={changeHandler}
        placeholder={firstName.placeHolder}
        error={formState.firstName.error}
        ModuleClassName={classes(styles.item, styles.itemFirstname)}
        onBlur={onBlurCalls}
        maxCharLength={firstName?.maxCharLength}
      />
      <Input
        type={lastName.type}
        id={lastName.id}
        name={lastName.name}
        label={lastName.label}
        value={formState.lastName.value}
        onChange={changeHandler}
        placeholder={lastName.placeHolder}
        error={formState.lastName.error}
        ModuleClassName={classes(styles.item, styles.itemLastname)}
        onBlur={onBlurCalls}
        maxCharLength={lastName?.maxCharLength}
      />
      <Input
        type={emailAddress.type}
        id={emailAddress.id}
        name={emailAddress.name}
        label={emailAddress.label}
        value={formState.emailAddress.value}
        onChange={changeHandler}
        placeholder={emailAddress.placeHolder}
        error={formState.emailAddress.error}
        ModuleClassName={styles.item}
        onBlur={onBlurCalls}
        maxCharLength={emailAddress?.maxCharLength}
      />

      <MaskedInputField
        id={phoneNumber.id}
        type={phoneNumber.type}
        name={phoneNumber.name}
        label={phoneNumber.label}
        value={formState.phoneNumber.value}
        onChange={changeHandler}
        error={formState.phoneNumber.error}
        ModuleClassName={classes(styles.item, styles.itemNumber)}
        onBlur={onBlurCalls}
      />
      <div className={`${styles.addressSection} mfrmFormGroup mb-0`}>
        {billingAddressConfigs?.autoSuggestionEnabled ? (
          <AddressInput
            formState={formState}
            addressFocus={addressFocus}
            onBlurHandler={onBlurHandler}
            updateFormState={updateFormState}
            handleFocus={handleFocus}
            prevRefValues={prevRefValues}
            stateProvinces={stateProvinces}
            maxCharLength={addressMaxLength}
            autoSuggestionOptions={billingAddressConfigs?.autoSuggestionOptions}
          />
        ) : (
          <Input
            type={address.type}
            id={address.id}
            name={address.name}
            label={address.label}
            value={formState.address.value}
            onChange={changeHandler}
            placeholder={address.placeHolder}
            error={formState.address.error}
            ModuleClassName={classes(styles.item, styles.itemAddress)}
            onBlur={onBlurCalls}
            maxCharLength={addressMaxLength}
          />
        )}
      </div>

      <Input
        type={apartment.type}
        id={apartment.id}
        name={apartment.name}
        label={apartment.label}
        value={formState.apartment.value}
        onChange={changeHandler}
        placeholder={apartment.placeHolder}
        error={formState.apartment.error}
        ModuleClassName={styles.item}
        onBlur={onBlurCalls}
        maxCharLength={apartmentMaxLength}
      />

      <Input
        type={zipcode.type}
        id={zipcode.id}
        name={zipcode.name}
        label={zipcode.label}
        value={zipCode}
        onChange={changeHandler}
        maxCharLength={zipcode?.maxCharLength}
        placeholder={zipcode.placeHolder}
        error={formState.zipcode.error}
        ModuleClassName={styles.item}
        onBlur={onBlurCalls}
      />

      <Input
        type={city.type}
        id={city.id}
        name={city.name}
        label={city.label}
        value={formState.city.value}
        onChange={changeHandler}
        placeholder={city.placeHolder}
        error={formState.city.error}
        ModuleClassName={classes(styles.item, styles.itemCity)}
        onBlur={onBlurCalls}
        maxCharLength={cityMaxLength}
      />

      <InputDropdown
        id={state.id}
        name={state.name}
        label={state.label}
        selectLabel={state.placeHolder}
        value={`${formState.state.value}`}
        onDropdownChange={changeHandler}
        error={formState.state.error}
        dropdownOptions={stateProvinces}
        ModuleClassName={classes(styles.item, styles.itemState)}
        onBlur={onBlurOnly}
        disabled={isZipcodeUpdating ?? false}
      />
    </form>
  );
};

export default AddressForm;
